import { Clock } from "lucide-react";

import type { Deal } from "@/api/client";
import { Button } from "@/components/ui/button";
import { FeedCard } from "@/components/ui/feed-card";
import { formatAud } from "@/lib/currency";

interface Props {
  deal: Deal;
  onManageExpiry: () => void;
  /**
   * Non-destructive [X]: acknowledges the expiry (same endpoint as the
   * overlay's "leave as is" path) WITHOUT opening the overlay. The deal
   * itself is untouched — the card just stops nagging.
   */
  onDismiss: () => void;
}

function daysRemaining(targetDate: string | null): number | null {
  if (!targetDate) return null;
  const diff = new Date(targetDate).getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/** "Expires today" / "Expires in 1 day" / "Expires in N days" (CHP-301). */
function expiresPhrase(days: number): string {
  if (days === 0) return "Expires today";
  if (days === 1) return "Expires in 1 day";
  return `Expires in ${days} days`;
}

/**
 * Expiring deal-registration feed card (CHP-301). Built on the shared
 * `FeedCard` primitive (CHP-523): the 4px left edge + eyebrow carry the lane
 * (coral when urgent ≤3 days, amber otherwise), the client name is the title,
 * and the deal ID / value / "Expires in N days" countdown form the detail row.
 */
export function ExpiryNotificationCard({ deal, onManageExpiry, onDismiss }: Props) {
  const days = daysRemaining(deal.target_close_date);
  if (days === null || deal.target_close_date === null) return null;

  const isUrgent = days <= 3;
  // Preserve the original null-token contract: a missing value hides the value
  // span entirely (the lib formatter would render "—" instead).
  const value = deal.total_contract_value != null ? formatAud(deal.total_contract_value) : null;

  return (
    // CHP-407: the whole card is clickable to open the expiry review options
    // (parity with the manager + outcome feed cards). The [X] dismiss and
    // [Review options] button stop propagation so they don't double-fire.
    <FeedCard
      tone={isUrgent ? "coral" : "amber"}
      eyebrow="Registration expiring"
      title={deal.client_name}
      description={
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <span>{deal.deal_id_display ?? "—"}</span>
          {value && <span>{value}</span>}
          <span
            className={`flex items-center gap-1 ${isUrgent ? "text-destructive" : "text-warning"}`}
          >
            <Clock className="h-3.5 w-3.5" aria-hidden />
            {expiresPhrase(days)}
          </span>
        </div>
      }
      onClick={onManageExpiry}
      ariaLabel={`Review expiry options for ${deal.client_name}`}
      onDismiss={onDismiss}
      dismissLabel={`Dismiss notification for ${deal.client_name}`}
      action={
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onManageExpiry();
          }}
        >
          Review options
        </Button>
      }
    />
  );
}
